/**
 * CLM-87 / R-87-1 (amended) / D-87-1 FINAL — the region dimension.
 *
 * The model extracts the counterparty's registered address and country from the
 * contract preamble as two verifiable facts; `region` is NEVER asked of the model.
 * The verified country goes through `regionForCountry` here — one fixed table, the
 * single source for the API (verification, analytics, search) and the web (selects,
 * tiles, the party lookup) — and sets `party.region` once (`WHERE region IS NULL`,
 * D-87-9) or by an admin override (D-87-10).
 *
 * Four regions, named as in Qfly (Gerry, 14 Sep 2026): North America = United
 * States and Canada; LATAM = Mexico, Central America, the Caribbean and South
 * America; EMEA = Europe incl. UK, the Middle East and Africa; APAC = Asia and
 * Oceania. `null` = Unassigned — only the uninhabited Antarctic territories in the
 * table, and any written country the alias map does not know.
 *
 * The CHECK literals in `packages/db/migrations/0094_party_region.sql` mirror
 * `REGIONS` and `REGION_SOURCES`; `sql-static-party-region.test.ts` reads this file
 * by path and asserts they agree.
 */
import { z } from 'zod';
export const REGIONS = ['North America', 'LATAM', 'APAC', 'EMEA'];
export const RegionSchema = z.enum(REGIONS);
export const REGION_SOURCES = ['verified_contract', 'admin'];
export const RegionSourceSchema = z.enum(REGION_SOURCES);
/** The two `field_verification` names CLM-87 adds to the queue (D-87-6). */
export const COUNTERPARTY_ADDRESS_FIELD = 'parties.counterparty.address';
export const COUNTERPARTY_COUNTRY_FIELD = 'parties.counterparty.country';
const NORTH_AMERICA = 'North America';
const LATAM = 'LATAM';
const APAC = 'APAC';
const EMEA = 'EMEA';
/**
 * ISO 3166-1 alpha-2 → region; `null` = Unassigned. All 249 codes are present —
 * `region.test.ts` carries the list and fails on a missing or extra code.
 * Mirrors `ck_party_region` (0094).
 */
export const COUNTRY_REGION = Object.freeze({
    // North America — United States and Canada (D-87-1 FINAL), plus the
    // North-American territories GL BM PM UM (geography; flagged in the handoff).
    US: NORTH_AMERICA, CA: NORTH_AMERICA, GL: NORTH_AMERICA, BM: NORTH_AMERICA, PM: NORTH_AMERICA, UM: NORTH_AMERICA,
    // LATAM — Mexico, Central America, the Caribbean and South America.
    MX: LATAM, BZ: LATAM, CR: LATAM, GT: LATAM, HN: LATAM, NI: LATAM, PA: LATAM, SV: LATAM,
    AG: LATAM, AI: LATAM, AW: LATAM, BB: LATAM, BL: LATAM, BQ: LATAM, BS: LATAM, CU: LATAM,
    CW: LATAM, DM: LATAM, DO: LATAM, GD: LATAM, GP: LATAM, HT: LATAM, JM: LATAM, KN: LATAM,
    KY: LATAM, LC: LATAM, MF: LATAM, MQ: LATAM, MS: LATAM, PR: LATAM, SX: LATAM, TC: LATAM,
    TT: LATAM, VC: LATAM, VG: LATAM, VI: LATAM,
    AR: LATAM, BO: LATAM, BR: LATAM, CL: LATAM, CO: LATAM, EC: LATAM, FK: LATAM, GF: LATAM,
    GY: LATAM, PE: LATAM, PY: LATAM, SR: LATAM, UY: LATAM, VE: LATAM, GS: LATAM,
    // EMEA — Europe incl. UK and its Crown dependencies and territories.
    GB: EMEA, IE: EMEA, IM: EMEA, JE: EMEA, GG: EMEA, GI: EMEA, FO: EMEA, AX: EMEA, SJ: EMEA,
    AD: EMEA, AL: EMEA, AT: EMEA, BA: EMEA, BE: EMEA, BG: EMEA, CH: EMEA, CZ: EMEA, DE: EMEA,
    DK: EMEA, EE: EMEA, ES: EMEA, FI: EMEA, FR: EMEA, GR: EMEA, HR: EMEA, HU: EMEA, IS: EMEA,
    IT: EMEA, LI: EMEA, LT: EMEA, LU: EMEA, LV: EMEA, MC: EMEA, ME: EMEA, MK: EMEA, MT: EMEA,
    NL: EMEA, NO: EMEA, PL: EMEA, PT: EMEA, RO: EMEA, RS: EMEA, SE: EMEA, SI: EMEA, SK: EMEA,
    SM: EMEA, VA: EMEA,
    // Russia, the Caucasus, Turkey, Cyprus.
    RU: EMEA, BY: EMEA, UA: EMEA, MD: EMEA, GE: EMEA, AM: EMEA, AZ: EMEA, TR: EMEA, CY: EMEA,
    // Middle East.
    AE: EMEA, BH: EMEA, IL: EMEA, IQ: EMEA, IR: EMEA, JO: EMEA, KW: EMEA, LB: EMEA, OM: EMEA,
    PS: EMEA, QA: EMEA, SA: EMEA, SY: EMEA, YE: EMEA,
    // Africa, incl. the islands.
    DZ: EMEA, AO: EMEA, BJ: EMEA, BW: EMEA, BF: EMEA, BI: EMEA, CM: EMEA, CV: EMEA, CF: EMEA,
    TD: EMEA, KM: EMEA, CG: EMEA, CD: EMEA, CI: EMEA, DJ: EMEA, EG: EMEA, GQ: EMEA, ER: EMEA,
    SZ: EMEA, ET: EMEA, GA: EMEA, GM: EMEA, GH: EMEA, GN: EMEA, GW: EMEA, KE: EMEA, LS: EMEA,
    LR: EMEA, LY: EMEA, MG: EMEA, MW: EMEA, ML: EMEA, MR: EMEA, MU: EMEA, YT: EMEA, MA: EMEA,
    MZ: EMEA, NA: EMEA, NE: EMEA, NG: EMEA, RE: EMEA, RW: EMEA, SH: EMEA, ST: EMEA, SN: EMEA,
    SC: EMEA, SL: EMEA, SO: EMEA, ZA: EMEA, SS: EMEA, SD: EMEA, TZ: EMEA, TG: EMEA, TN: EMEA,
    UG: EMEA, EH: EMEA, ZM: EMEA, ZW: EMEA,
    // APAC — Asia.
    AF: APAC, PK: APAC, IN: APAC, BD: APAC, LK: APAC, NP: APAC, BT: APAC, MV: APAC,
    CN: APAC, HK: APAC, MO: APAC, TW: APAC, JP: APAC, KR: APAC, KP: APAC, MN: APAC,
    BN: APAC, KH: APAC, ID: APAC, LA: APAC, MY: APAC, MM: APAC, PH: APAC, SG: APAC, TH: APAC,
    TL: APAC, VN: APAC,
    KZ: APAC, KG: APAC, TJ: APAC, TM: APAC, UZ: APAC,
    // Oceania and the Pacific.
    AU: APAC, NZ: APAC, PG: APAC, FJ: APAC, SB: APAC, VU: APAC, NC: APAC, PF: APAC, WS: APAC,
    TO: APAC, KI: APAC, FM: APAC, MH: APAC, PW: APAC, NR: APAC, TV: APAC, GU: APAC, AS: APAC,
    MP: APAC, WF: APAC, CK: APAC, NU: APAC, TK: APAC, PN: APAC,
    // Indian Ocean and Australian external territories.
    IO: APAC, CX: APAC, CC: APAC, NF: APAC, HM: APAC,
    // Unassigned — uninhabited Antarctic territories.
    AQ: null, BV: null, TF: null,
});
/**
 * Written country → ISO alpha-2, as authored: every ISO short name plus the
 * ways a preamble actually writes a country. Keys may carry apostrophes and
 * diacritics; `COUNTRY_NAMES` below runs each one through the same `normalise()`
 * the lookup applies to the input, so an authored key can never be unreachable.
 */
const COUNTRY_NAMES_RAW = Object.freeze({
    // North America
    'united states': 'US', 'united states of america': 'US', usa: 'US', us: 'US', 'u s a': 'US', 'u s': 'US',
    america: 'US', canada: 'CA', greenland: 'GL', bermuda: 'BM', 'saint pierre and miquelon': 'PM',
    'united states minor outlying islands': 'UM',
    // LATAM
    mexico: 'MX', méxico: 'MX', belize: 'BZ', 'costa rica': 'CR', guatemala: 'GT', honduras: 'HN',
    nicaragua: 'NI', panama: 'PA', 'el salvador': 'SV', 'antigua and barbuda': 'AG', anguilla: 'AI',
    aruba: 'AW', barbados: 'BB', 'saint barthélemy': 'BL', 'saint barthelemy': 'BL',
    'bonaire sint eustatius and saba': 'BQ', bahamas: 'BS', 'the bahamas': 'BS', cuba: 'CU', curaçao: 'CW',
    curacao: 'CW', dominica: 'DM', 'dominican republic': 'DO', grenada: 'GD', guadeloupe: 'GP',
    haiti: 'HT', jamaica: 'JM', 'saint kitts and nevis': 'KN', 'cayman islands': 'KY', 'saint lucia': 'LC',
    'saint martin': 'MF', martinique: 'MQ', montserrat: 'MS', 'puerto rico': 'PR', 'sint maarten': 'SX',
    'turks and caicos islands': 'TC', 'trinidad and tobago': 'TT', 'saint vincent and the grenadines': 'VC',
    'british virgin islands': 'VG', 'virgin islands british': 'VG', 'us virgin islands': 'VI',
    'virgin islands us': 'VI', 'united states virgin islands': 'VI',
    argentina: 'AR', bolivia: 'BO', brazil: 'BR', brasil: 'BR', chile: 'CL', colombia: 'CO', ecuador: 'EC',
    'falkland islands': 'FK', 'french guiana': 'GF', guyana: 'GY', peru: 'PE', perú: 'PE', paraguay: 'PY',
    suriname: 'SR', uruguay: 'UY', venezuela: 'VE', 'south georgia and the south sandwich islands': 'GS',
    // EMEA — UK and Europe
    'united kingdom': 'GB', uk: 'GB', 'u k': 'GB', england: 'GB', scotland: 'GB', wales: 'GB',
    'northern ireland': 'GB', 'great britain': 'GB', britain: 'GB',
    'united kingdom of great britain and northern ireland': 'GB', 'england and wales': 'GB',
    ireland: 'IE', 'republic of ireland': 'IE', 'isle of man': 'IM', jersey: 'JE', guernsey: 'GG',
    gibraltar: 'GI', 'faroe islands': 'FO', 'åland islands': 'AX', 'aland islands': 'AX',
    'svalbard and jan mayen': 'SJ', andorra: 'AD', albania: 'AL', austria: 'AT',
    'bosnia and herzegovina': 'BA', belgium: 'BE', bulgaria: 'BG', switzerland: 'CH', czechia: 'CZ',
    'czech republic': 'CZ', germany: 'DE', deutschland: 'DE', denmark: 'DK', estonia: 'EE', spain: 'ES',
    españa: 'ES', finland: 'FI', france: 'FR', greece: 'GR', croatia: 'HR', hungary: 'HU', iceland: 'IS',
    italy: 'IT', italia: 'IT', liechtenstein: 'LI', lithuania: 'LT', luxembourg: 'LU', latvia: 'LV',
    monaco: 'MC', montenegro: 'ME', 'north macedonia': 'MK', macedonia: 'MK', malta: 'MT',
    netherlands: 'NL', 'the netherlands': 'NL', holland: 'NL', norway: 'NO', poland: 'PL', portugal: 'PT',
    romania: 'RO', serbia: 'RS', sweden: 'SE', slovenia: 'SI', slovakia: 'SK', 'san marino': 'SM',
    'holy see': 'VA', 'vatican city': 'VA',
    russia: 'RU', 'russian federation': 'RU', belarus: 'BY', ukraine: 'UA', moldova: 'MD',
    'republic of moldova': 'MD', georgia: 'GE', armenia: 'AM', azerbaijan: 'AZ', turkey: 'TR', türkiye: 'TR',
    turkiye: 'TR', cyprus: 'CY',
    // EMEA — Middle East
    'united arab emirates': 'AE', uae: 'AE', 'u a e': 'AE', emirates: 'AE', dubai: 'AE', bahrain: 'BH',
    israel: 'IL', iraq: 'IQ', iran: 'IR', jordan: 'JO', kuwait: 'KW', lebanon: 'LB', oman: 'OM',
    palestine: 'PS', 'state of palestine': 'PS', qatar: 'QA', 'saudi arabia': 'SA',
    'kingdom of saudi arabia': 'SA', ksa: 'SA', syria: 'SY', 'syrian arab republic': 'SY', yemen: 'YE',
    // EMEA — Africa
    algeria: 'DZ', angola: 'AO', benin: 'BJ', botswana: 'BW', 'burkina faso': 'BF', burundi: 'BI',
    cameroon: 'CM', 'cabo verde': 'CV', 'cape verde': 'CV', 'central african republic': 'CF', chad: 'TD',
    comoros: 'KM', congo: 'CG', 'republic of the congo': 'CG', 'democratic republic of the congo': 'CD',
    'dr congo': 'CD', "côte d'ivoire": 'CI', 'côte divoire': 'CI', 'cote divoire': 'CI', 'ivory coast': 'CI',
    djibouti: 'DJ', egypt: 'EG', 'equatorial guinea': 'GQ', eritrea: 'ER', eswatini: 'SZ', swaziland: 'SZ',
    ethiopia: 'ET', gabon: 'GA', gambia: 'GM', 'the gambia': 'GM', ghana: 'GH', guinea: 'GN',
    'guinea bissau': 'GW', kenya: 'KE', lesotho: 'LS', liberia: 'LR', libya: 'LY', madagascar: 'MG',
    malawi: 'MW', mali: 'ML', mauritania: 'MR', mauritius: 'MU', mayotte: 'YT', morocco: 'MA',
    mozambique: 'MZ', namibia: 'NA', niger: 'NE', nigeria: 'NG', réunion: 'RE', reunion: 'RE', rwanda: 'RW',
    'saint helena ascension and tristan da cunha': 'SH', 'saint helena': 'SH', 'sao tome and principe': 'ST',
    'são tomé and príncipe': 'ST', senegal: 'SN', seychelles: 'SC', 'sierra leone': 'SL', somalia: 'SO',
    'south africa': 'ZA', 'republic of south africa': 'ZA', 'south sudan': 'SS', sudan: 'SD', tanzania: 'TZ',
    'united republic of tanzania': 'TZ', togo: 'TG', tunisia: 'TN', uganda: 'UG', 'western sahara': 'EH',
    zambia: 'ZM', zimbabwe: 'ZW',
    // APAC — Asia
    afghanistan: 'AF', pakistan: 'PK', india: 'IN', bharat: 'IN', bangladesh: 'BD', 'sri lanka': 'LK',
    nepal: 'NP', bhutan: 'BT', maldives: 'MV', china: 'CN', "people's republic of china": 'CN',
    'peoples republic of china': 'CN', prc: 'CN', 'hong kong': 'HK', 'hong kong sar': 'HK', macao: 'MO',
    macau: 'MO', taiwan: 'TW', 'republic of china': 'TW', japan: 'JP', 'south korea': 'KR',
    'republic of korea': 'KR', korea: 'KR', 'north korea': 'KP', "democratic people's republic of korea": 'KP',
    mongolia: 'MN', brunei: 'BN', 'brunei darussalam': 'BN', cambodia: 'KH', indonesia: 'ID', laos: 'LA',
    "lao people's democratic republic": 'LA', malaysia: 'MY', myanmar: 'MM', burma: 'MM', philippines: 'PH',
    'the philippines': 'PH', singapore: 'SG', thailand: 'TH', 'timor leste': 'TL', 'east timor': 'TL',
    vietnam: 'VN', 'viet nam': 'VN', kazakhstan: 'KZ', kyrgyzstan: 'KG', tajikistan: 'TJ', turkmenistan: 'TM',
    uzbekistan: 'UZ',
    // APAC — Oceania
    australia: 'AU', 'new zealand': 'NZ', aotearoa: 'NZ', 'papua new guinea': 'PG', fiji: 'FJ',
    'solomon islands': 'SB', vanuatu: 'VU', 'new caledonia': 'NC', 'french polynesia': 'PF', samoa: 'WS',
    tonga: 'TO', kiribati: 'KI', micronesia: 'FM', 'federated states of micronesia': 'FM',
    'marshall islands': 'MH', palau: 'PW', nauru: 'NR', tuvalu: 'TV', guam: 'GU', 'american samoa': 'AS',
    'northern mariana islands': 'MP', 'wallis and futuna': 'WF', 'cook islands': 'CK', niue: 'NU',
    tokelau: 'TK', pitcairn: 'PN', 'pitcairn islands': 'PN', 'british indian ocean territory': 'IO',
    'christmas island': 'CX', 'cocos keeling islands': 'CC', 'cocos islands': 'CC', 'norfolk island': 'NF',
    'heard island and mcdonald islands': 'HM',
    // Unassigned
    antarctica: 'AQ', 'bouvet island': 'BV', 'french southern territories': 'TF',
});
/** `COUNTRY_NAMES_RAW` keyed by `normalise(key)` — the table the lookup reads. */
const COUNTRY_NAMES = Object.freeze(Object.fromEntries(Object.entries(COUNTRY_NAMES_RAW).map(([written, code]) => [normalise(written), code])));
/** Lower-case, punctuation → space, single spaces, trimmed. Never throws. */
function normalise(written) {
    return written
        .toLowerCase()
        .replace(/[.,;:()[\]"'’`\-_/\\]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}
/**
 * The written country → its ISO 3166-1 alpha-2 code, or `null` when nothing
 * recognises it. The lookup `regionForCountry` has always done, exposed for
 * CLM-113 (R-113-24): a vendor sheet's `Country/Region Code` goes through it, so
 * `UK` becomes `GB` (flagged `country_code_normalised`) and an unknown code is
 * dropped (`country_code_unrecognised`) rather than stored. Whole-value match
 * only, exactly as below; a bare code counts only when it is one of the 249 in
 * `COUNTRY_REGION`, so `'XX'` is `null`. Never throws.
 */
export function countryCodeFor(written) {
    if (typeof written !== 'string')
        return null;
    const key = normalise(written);
    if (key.length === 0)
        return null;
    const code = COUNTRY_NAMES[key] ?? (key.length === 2 ? key.toUpperCase() : undefined);
    if (code === undefined || !Object.hasOwn(COUNTRY_REGION, code))
        return null;
    return code;
}
/**
 * The written country → a region, or `null` when unknown / unassigned (D-87-8).
 * Whole-value match only: `'England'` → EMEA, `'Milton Keynes, England'` → null —
 * the function does not substring-search, so city text is never mistaken for a
 * country. A bare ISO alpha-2 code (`'GB'`) is accepted too. Never throws.
 */
export function regionForCountry(written) {
    const code = countryCodeFor(written);
    return code === null ? null : (COUNTRY_REGION[code] ?? null);
}
//# sourceMappingURL=region.js.map